import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { Instance } from '../types';
import { InstanceEditModal } from '../components/InstanceEditModal';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import './InstanceDetailPage.css';

export const InstanceDetailPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetRequestError, setResetRequestError] = useState<string | null>(null);
  const [qwcMessage, setQwcMessage] = useState<string | null>(null);
  const [qwcError, setQwcError] = useState<string | null>(null);
  const [showQbdPathInfo, setShowQbdPathInfo] = useState(false);
  const [showAppUrlInfo, setShowAppUrlInfo] = useState(false);
  const [showSyncFreqInfo, setShowSyncFreqInfo] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (instanceId) {
      fetchInstance(instanceId);
    }
  }, [instanceId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInstance = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getInstanceById(id);
      setInstance(data);
    } catch (err) {
      setError('Failed to load instance details. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendQwcFile = async () => {
    setQwcMessage(null);
    setQwcError(null);
    setIsMenuOpen(false);
    try {
      const result = await api.sendQwcFile(instanceId!);
      setQwcMessage(result.message);
    } catch (err) {
      setQwcError(err instanceof Error ? err.message : 'Request failed. Please try again.');
    }
  };

  const handleResetQBWCPassword = async () => {
    setResetRequestError(null);
    setIsMenuOpen(false);
    try {
      await api.forgotPassword(instanceId!);
      setIsResetModalOpen(true);
    } catch (err) {
      setResetRequestError(err instanceof Error ? err.message : 'Request failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="instance-detail-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading instance details...</p>
        </div>
      </div>
    );
  }

  if (error || !instance) {
    return (
      <div className="instance-detail-page">
        <div className="error">
          <p>{error || 'Instance not found'}</p>
          <button onClick={() => navigate('/instances')} className="back-button">
            Back to Instances
          </button>
        </div>
      </div>
    );
  }

  const canSendQwc = !!(instance.qbdPath && instance.appUrl && instance.frequencyInMinutes);

  return (
    <div className="instance-detail-page">
      <div className="page-header">
        <Link to="/instances" className="back-link">
          ← Back to Instances
        </Link>
        <div className="header-content">
          <h1>{instance.name}</h1>
          <span className={`status-badge ${instance.validated ? 'validated' : 'not-validated'}`}>
            {instance.validated ? 'Validated' : 'Not Validated'}
          </span>
        </div>
      </div>

      <div className="detail-sections">
        <section className="detail-section">
          <div className="section-title-row">
            <h3 className="section-title">General Information</h3>
            <div className="instance-actions" ref={menuRef}>
              <button
                className="kebab-menu-button"
                onClick={() => setIsMenuOpen(v => !v)}
                aria-label="Instance actions"
                aria-haspopup="true"
                aria-expanded={isMenuOpen}
              >
                &#8942;
              </button>
              {isMenuOpen && (
                <div className="kebab-menu-dropdown" role="menu">
                  <button
                    className="kebab-menu-item"
                    role="menuitem"
                    onClick={() => { setIsEditModalOpen(true); setIsMenuOpen(false); }}
                  >
                    Edit Instance
                  </button>
                  <Link
                    to={`/instances/${instance.id}/dashboard`}
                    className="kebab-menu-item"
                    role="menuitem"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to={`/instances/${instance.id}/monitoring`}
                    className="kebab-menu-item"
                    role="menuitem"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Monitoring
                  </Link>
                  <button
                    className="kebab-menu-item"
                    role="menuitem"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Test Connection
                  </button>
                  <button
                    className="kebab-menu-item"
                    role="menuitem"
                    onClick={handleSendQwcFile}
                    disabled={!canSendQwc}
                  >
                    Send QWC File
                  </button>
                  <button
                    className="kebab-menu-item"
                    role="menuitem"
                    onClick={handleResetQBWCPassword}
                  >
                    Reset QBWC Password
                  </button>
                  <div className="kebab-menu-divider" />
                  <button
                    className="kebab-menu-item danger"
                    role="menuitem"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Delete Instance
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="info-grid">
            <div className="info-item-row">
              <div className="info-item">
                <label className="info-label">Instance Name</label>
                <div className="info-value">{instance.name}</div>
              </div>
              <div className="info-item">
                <label className="info-label">Validation Status</label>
                <div className="info-value">
                  <span className={`status-badge ${instance.validated ? 'validated' : 'not-validated'}`}>
                    {instance.validated ? '✓ Validated' : '✗ Not Validated'}
                  </span>
                </div>
              </div>
            </div>
            <div className="info-item">
              <label className="info-label">Instance ID</label>
              <div className="info-value code">{instance.id}</div>
            </div>
            <div className="info-item">
              <label className="info-label">Customer ID</label>
              <div className="info-value">{instance.customerId || 'N/A'}</div>
            </div>
            <div className="info-item">
              <label className="info-label">Email</label>
              <div className="info-value">
                <a href={`mailto:${instance.email}`}>{instance.email}</a>
              </div>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h3 className="section-title">QuickBooks Configuration</h3>
          <div className="info-grid">
            <div className="info-item full-width">
              <label className="info-label">
                QuickBooks File Path
                <span className="info-icon-wrapper">
                  <button
                    className="info-icon"
                    onClick={() => setShowQbdPathInfo(v => !v)}
                    aria-label="QuickBooks File Path information"
                  >ⓘ</button>
                  {showQbdPathInfo && (
                    <span className="info-popover">
                      The QuickBooks File Path is the location of the QuickBooks data (QBW) file. The location of the file is usually in C:\Program Files\Intuit\QuickBooks\ on the server or desktop where QuickBooks Desktop was installed.
                    </span>
                  )}
                </span>
              </label>
              <div className="info-value code">{instance.qbdPath}</div>
            </div>
            <div className="info-item full-width">
              <label className="info-label">
                Application URL
                <span className="info-icon-wrapper">
                  <button
                    className="info-icon"
                    onClick={() => setShowAppUrlInfo(v => !v)}
                    aria-label="Application URL information"
                  >ⓘ</button>
                  {showAppUrlInfo && (
                    <span className="info-popover">
                      This field should only be modified by Support. If this value is changed to something other than a valid QBD Connect endpoint, it will cause communication with QBWC to fail.
                    </span>
                  )}
                </span>
              </label>
              <div className="info-value">
                <a href={instance.appUrl} target="_blank" rel="noopener noreferrer">
                  {instance.appUrl}
                </a>
              </div>
            </div>
            <div className="info-item">
              <label className="info-label">
                Sync Frequency
                <span className="info-icon-wrapper">
                  <button
                    className="info-icon"
                    onClick={() => setShowSyncFreqInfo(v => !v)}
                    aria-label="Sync Frequency information"
                  >ⓘ</button>
                  {showSyncFreqInfo && (
                    <span className="info-popover">
                      This value determines how often QBWC contacts QBD Connect to ask for requests.
                    </span>
                  )}
                </span>
              </label>
              <div className="info-value">{instance.frequencyInMinutes} minutes</div>
            </div>
          </div>
        </section>
      </div>

      {qwcMessage && (
        <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{qwcMessage}</div>
      )}
      {qwcError && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{qwcError}</div>
      )}
      {resetRequestError && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{resetRequestError}</div>
      )}

      <InstanceEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        instance={instance}
        onSave={() => fetchInstance(instanceId!)}
      />

      <ResetPasswordModal
        isOpen={isResetModalOpen}
        username={instanceId!}
        onClose={() => setIsResetModalOpen(false)}
      />
    </div>
  );
};
